import { cached } from '@glimmer/tracking';

import type { DSData, DSSetup } from 'webapp-types';
import type { CompareFunction } from 'webapp/enums/enums';
import { calculateAndFormatChange, calculateChange, getIndicatorClass } from 'webapp/utils/number';

import type DataSelect from 'webapp/models-custom/data-select';
import DsDataOutputCompare from 'webapp/models-custom/ds-data/output/compare';
import DsDataOutputOther from 'webapp/models-custom/ds-data/output/other';
import DsDataRootBase from 'webapp/models-custom/ds-data/root/base';

export default class DsDataRootCompare extends DsDataRootBase {
	override outputs: DsDataOutputCompare[];

	index: number; // will get ds compare setup with it
	function: CompareFunction;

	isTop = false; // to distinguish from top root without instanceof

	constructor(dataSelect: DataSelect, rawData: DSData.RootCompare, index: number) {
		super(dataSelect, rawData);

		this.index = index;
		this.function = rawData.function;

		this.outputs = rawData.data.map((d) => new DsDataOutputCompare(this, d));

		this.outputsOther = (rawData.others || []).map((d) => new DsDataOutputOther(this, d));
	}

	get compareSettings(): DSSetup.Compare | null {
		return this.dataSelect.compares[this.index] || null;
	}

	@cached
	get totalSumChange(): number | null {
		const dataRoot = this.dataSelect.dataRoot,
			compareTotalSumValue = this.totalSumValue,
			mainTotalSumValue = dataRoot ? dataRoot.totalSumValue : null;
		return calculateChange(compareTotalSumValue, mainTotalSumValue, this.function);
	}

	@cached
	get totalSumChangeFormatted(): string {
		const dataRoot = this.dataSelect.dataRoot,
			compareTotalSumValue = this.totalSumValue,
			mainTotalSumValue = dataRoot ? dataRoot.totalSumValue : null;
		return calculateAndFormatChange(
			compareTotalSumValue,
			mainTotalSumValue,
			this.format,
			this.function,
		);
	}

	@cached
	get totalSumIndicatorClass(): string {
		const compareTotalSumValue = this.totalSumValue,
			mainTotalSumValue = this.dataSelect.dataRoot?.totalSumValue;
		if (typeof compareTotalSumValue === 'number' && typeof mainTotalSumValue === 'number') {
			const change = mainTotalSumValue - compareTotalSumValue;
			return getIndicatorClass(change, this.invert);
		} else {
			return '';
		}
	}

	override getOutputByDimensionValuesAndUnit(
		dimensionValues: DSData.DimensionsAndValuesMap,
		unit: string | null,
	) {
		return super.getOutputByDimensionValuesAndUnit(
			dimensionValues,
			unit,
		) as DsDataOutputCompare | null;
	}
}
